import {
  Component,
  EventEmitter,
  Inject,
  Output,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { TuiHostedDropdownComponent, TuiAlertService } from '@taiga-ui/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { AssignerService } from 'src/app/services/assigner/assigner.service';
import { ServicesService } from 'src/app/services/service/services.service';
import { TranslateService } from '@ngx-translate/core';

import {
  AssignmentExecution,
  Zone,
  ZonesResponse,
  ApplyAssignmentRequest,
  PaginatedAssignmentsRequest,
  GetPaginatedAlgorithmicExecutionsResponse,
} from './models/assignment.model';
import {
  AssignmentStatus,
  SortDirection,
  TableSize,
  SortableColumn,
} from './types/assignment.types';

import { TABLE_CONFIG, SEARCH_CONFIG, REGEX_PATTERNS } from './constants';
import { DateFormatter, CsvFormatter, ZoneHelper, SortHelper } from './utils';
import { STATUS_CONFIG, StatusConfig } from './models';

type AssignmentExecutionView = AssignmentExecution & {
  executionDateFormatted?: string;
  hour?: string;
  assignmentDateFormatted?: string;
  zoneName?: string;
  executorName?: string;
};

@Component({
  selector: 'app-assigner-table',
  templateUrl: './assigner-table.component.html',
  styleUrls: ['./assigner-table.component.css'],
})
export class AssignerTableComponent implements OnInit, OnDestroy {
  @ViewChild('warning_assigner_modal') warningModal!: TemplateRef<void>;
  @ViewChild(TuiHostedDropdownComponent)
  dropdownComponent?: TuiHostedDropdownComponent;

  @Output() assignamentId = new EventEmitter<string>();

  private readonly UUID_REGEX = REGEX_PATTERNS.UUID;
  private readonly SEARCH_DEBOUNCE_TIME = SEARCH_CONFIG.DEBOUNCE_TIME;
  private readonly PAGE_SIZE = TABLE_CONFIG.PAGE_SIZE;

  readonly sizes = TABLE_CONFIG.SIZES;
  readonly columns = TABLE_CONFIG.COLUMNS;
  readonly Math = Math;

  readonly statusConfig: Record<AssignmentStatus, StatusConfig> = STATUS_CONFIG;

  // Data
  private allAssignments: AssignmentExecutionView[] = [];
  paginatedData: AssignmentExecutionView[] = [];
  zones: Zone[] = [];

  // UI state
  size: TableSize = this.sizes[0];
  selectedZoneId?: string;
  filterCount = 0;
  open = false;
  isLoading = false;
  hasLoadedOnce = false;

  // Export
  exportCount = 0;
  isXlsxSelected = false;
  isCsvSelected = false;

  // Sorting
  sortedColumn: SortableColumn | null = null;
  sortDirection: SortDirection | null = null;

  // Pagination
  index = 0; // UI 0-based
  length = 0; // pages count
  pageSize = this.PAGE_SIZE;
  totalCount = 0; // server totalItems

  // Modal
  bsModalRef?: BsModalRef;
  toApplyAssignamentId = '';
  toApplyAssignamentDate = '';
  isApplyingAssignment = false;

  // Reactive
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(TuiAlertService) private readonly alerts: TuiAlertService,
    private readonly servicesSvc: ServicesService,
    private readonly assignerService: AssignerService,
    private readonly translate: TranslateService,
    private readonly modalService: BsModalService
  ) {}

  // Template getters
  get hasData(): boolean {
    return this.totalCount > 0;
  }

  get hasFilters(): boolean {
    return !!this.selectedZoneId;
  }

  get showEmptyState(): boolean {
    return !this.isLoading && this.hasLoadedOnce && !this.hasData;
  }

  get showNoResultsState(): boolean {
    return (
      !this.isLoading &&
      this.hasLoadedOnce &&
      this.hasData &&
      this.paginatedData.length === 0 &&
      this.hasFilters
    );
  }

  get showTable(): boolean {
    return this.paginatedData.length > 0;
  }

  get totalCountDisplayed(): number {
    return this.totalCount;
  }

  get emptyStateMessage(): string {
    if (this.showNoResultsState) {
      return this.getNoResultsMessage();
    }
    // translated literal
    return this.translate.instant('assigner.no_assignments_available');
  }

  private getNoResultsMessage(): string {
    const filters: string[] = [];
    if (this.selectedZoneId) {
      const zoneName = ZoneHelper.getZoneName(this.selectedZoneId, this.zones);
      filters.push(`zona "${zoneName}"`);
    }
    // keep the template of message but translate the prefix
    const base = this.translate.instant('assigner.no_results_title');
    return `${this.translate.instant(
      'assigner.no_results_title'
    )}: ${filters.join(' y ')}`;
  }

  // Lifecycle
  async ngOnInit(): Promise<void> {
    this.setupSearchSubscription();
    await this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchSubscription(): void {
    this.searchSubject
      .pipe(
        debounceTime(this.SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => void this.fetchPage(0));
  }

  private async loadInitialData(): Promise<void> {
    this.setLoading(true);
    try {
      const zonesResponse: ZonesResponse = await firstValueFrom(
        this.servicesSvc.getZones()
      );
      this.zones = zonesResponse.zones || [];
      await this.fetchPage(0);
      this.hasLoadedOnce = true;
    } catch (err) {
      console.error('Error loading initial data:', err);
      this.showErrorAlert(
        this.translate.instant('assigner.error_loading_data')
      );
      this.hasLoadedOnce = true;
    } finally {
      this.setLoading(false);
    }
  }

  private async fetchPage(pageIndex: number): Promise<void> {
    this.setLoading(true);
    try {
      const request: PaginatedAssignmentsRequest = {
        pageIndex,
        pageSize: this.pageSize,
      };
      if (this.selectedZoneId) request.zoneGuid = this.selectedZoneId;

      const resp: GetPaginatedAlgorithmicExecutionsResponse =
        await firstValueFrom(
          this.assignerService.getAssignmentsPaginated(request)
        );

      const serverExecutions = Array.isArray(resp.executions)
        ? resp.executions
        : [];

      const unknown = this.translate.instant('assigner.unknown');

      const viewItems: AssignmentExecutionView[] = serverExecutions.map((s) => {
        const item: AssignmentExecutionView = {
          executionId: s.executionId,
          executionDate:
            s.executionDate as unknown as AssignmentExecution['executionDate'],
          executorId: s.executorId ?? '',
          zoneId: s.zoneId ?? '',
          executionTargetDate:
            s.executionTargetDate as unknown as AssignmentExecution['executionTargetDate'],
          status: s.status as unknown as string,
          doWeHaveSweepers: s.doWeHaveSweepers ?? false,
          executionDateFormatted: DateFormatter.formatDate(s.executionDate ?? ''),
          hour: DateFormatter.formatTime(s.executionDate ?? ''),
          assignmentDateFormatted: DateFormatter.formatDatePreservingDay(
            s.executionTargetDate ?? ''
          ),
          zoneName: ZoneHelper.getZoneName(s.zoneId ?? '', this.zones),
          executorName: s.executorName?.length > 0 ? s.executorName : unknown,
        };
        return item;
      });

      this.allAssignments = viewItems;
      this.totalCount = Number(resp.totalItems ?? viewItems.length);
      this.length = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
      this.index = pageIndex;
      this.applyFilters();
      this.hasLoadedOnce = true;
    } catch (err) {
      console.error('Error fetching paginated assignments:', err);
      this.showErrorAlert(
        this.translate.instant('assigner.error_fetching_paginated')
      );
    } finally {
      this.setLoading(false);
    }
  }

  private applyFilters(): void {
    let filtered = [...this.allAssignments];

    if (this.selectedZoneId) {
      filtered = filtered.filter((item) => item.zoneId === this.selectedZoneId);
    }

    this.filterCount = filtered.length;
    this.applySorting(filtered);
    this.updatePagination();
  }

  private applySorting(data: AssignmentExecutionView[]): void {
    if (this.sortedColumn === null || this.sortDirection === null) {
      // No sort active: keep server order (page-level)
      this.paginatedData = data.slice(0, this.pageSize);
      return;
    }

    const activeColumn = this.sortedColumn as SortableColumn;
    const activeDirection = this.sortDirection as SortDirection;

    const sorted = [...data].sort((a, b) => {
      const valueA = a[
        activeColumn as keyof AssignmentExecutionView
      ] as unknown;
      const valueB = b[
        activeColumn as keyof AssignmentExecutionView
      ] as unknown;

      if (SortHelper.isDateColumn(activeColumn)) {
        return DateFormatter.compareDates(
          String(valueA ?? ''),
          String(valueB ?? ''),
          activeDirection
        );
      }

      if (SortHelper.isTimeColumn(activeColumn)) {
        return SortHelper.compareTime(
          String(valueA ?? ''),
          String(valueB ?? ''),
          activeDirection
        );
      }

      return SortHelper.compareValues(
        valueA as string | number | boolean | undefined,
        valueB as string | number | boolean | undefined,
        activeDirection
      );
    });

    this.paginatedData = sorted.slice(0, this.pageSize);
  }

  private updatePagination(): void {
    this.length = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    if (this.index >= this.length && this.length > 0) this.index = 0;
  }

  // UI handlers that trigger backend calls (search & zone -> server)
  onSearchChange(searchText: string): void {
    this.index = 0;
    this.searchSubject.next(searchText);
  }

  async onZoneChange(zoneId?: string): Promise<void> {
    this.selectedZoneId = zoneId;
    this.index = 0;
    await this.fetchPage(0);
  }

  async goToPage(pageIndex: number): Promise<void> {
    await this.fetchPage(pageIndex);
  }

  clearFilters(): void {
    this.selectedZoneId = undefined;
    this.index = 0;
    void this.fetchPage(0);
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  private showErrorAlert(message: string): void {
    this.alerts
      .open(message, {
        label: this.translate.instant('assigner.label_error'),
        status: 'error',
        autoClose: true,
      })
      .subscribe();
  }

  private showSuccessAlert(message: string): void {
    this.alerts
      .open(message, {
        label: this.translate.instant('assigner.label_success'),
        status: 'success',
        autoClose: true,
      })
      .subscribe();
  }

  async refresh(): Promise<void> {
    await this.fetchPage(this.index);
    this.showSuccessAlert(this.translate.instant('assigner.data_refreshed'));
  }

  updateExportCount(): void {
    this.exportCount = 0;
    if (this.isXlsxSelected) this.exportCount++;
    if (this.isCsvSelected) this.exportCount++;
  }

  exportTo(): void {
    if (this.paginatedData.length === 0) {
      this.showErrorAlert(this.translate.instant('assigner.no_data_to_export'));
      return;
    }
    if (this.isXlsxSelected) this.exportToXLSX();
    if (this.isCsvSelected) this.exportToCSV();
    if (this.isXlsxSelected || this.isCsvSelected) {
      this.showSuccessAlert(
        this.translate.instant('assigner.export_completed')
      );
      this.open = false;
    }
  }

  exportToXLSX(): void {
    const dataToExport = this.paginatedData;
    const headers = [
      this.translate.instant('assigner.date_excute_input_table'), // Fecha de ejecución
      this.translate.instant('assigner.hour_input_table'), // Hora (executionDate)
      this.translate.instant('assigner.executor_input_table'),
      this.translate.instant('assigner.status_input_table'),
      this.translate.instant('assigner.zone_input_table'),
      this.translate.instant('assigner.date_assigned_input_table'), // Fecha asignación (executionTargetDate)
    ];

    const unknown = this.translate.instant('assigner.unknown');

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
      dataToExport.map((item) => {
        return {
          [headers[0]]: DateFormatter.formatDate(item.executionDate ?? ''), // executionDate -> date
          [headers[1]]: DateFormatter.formatTime(item.executionDate ?? ''), // executionDate -> time
          [headers[2]]: item.executorName ?? unknown,
          [headers[3]]: this.translateStatus(item.status),
          [headers[4]]: ZoneHelper.getZoneName(item.zoneId ?? '', this.zones),
          [headers[5]]: DateFormatter.formatDatePreservingDay(
            item.executionTargetDate ?? ''
          ), // executionTargetDate -> assignment date
        };
      }),
      { header: headers }
    );

    ws['!cols'] = [
      { wch: 15 },
      { wch: 10 },
      { wch: 15 },
      { wch: 20 },
      { wch: 10 },
      { wch: 20 },
    ];

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const wbout: ArrayBuffer = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });
    saveAs(
      new Blob([wbout], { type: 'application/octet-stream' }),
      'data.xlsx'
    );
  }

  exportToCSV(): void {
    const headers = [
      this.translate.instant('assigner.date_excute_input_table'),
      this.translate.instant('assigner.hour_input_table'),
      this.translate.instant('assigner.executor_input_table'),
      this.translate.instant('assigner.status_input_table'),
      this.translate.instant('assigner.zone_input_table'),
      this.translate.instant('assigner.date_assigned_input_table'),
    ];

    const rows = this.paginatedData.map((item) => [
      DateFormatter.formatDate(item.executionDate ?? ''),
      DateFormatter.formatTime(item.executionDate ?? ''),
      item.executorName ?? this.translate.instant('assigner.unknown'),
      this.translateStatus(item.status),
      ZoneHelper.getZoneName(item.zoneId ?? '', this.zones),
      DateFormatter.formatDatePreservingDay(item.executionTargetDate ?? ''),
    ]);

    const csvContent = CsvFormatter.createCsvContent(headers, rows);
    CsvFormatter.download(csvContent, 'data');
  }

  private translateStatus(status?: string): string {
    if (!status) return this.translate.instant('assigner.status_no_status');

    const statusMap: Record<string, string> = {
      InProgress: 'assigner.status_in_progress',
      Completed: 'assigner.status_completed',
      Failed: 'assigner.status_failed',
      Applied: 'assigner.status_applied',
      Initialized: 'assigner.status_initialized',
    };

    return this.translate.instant(
      statusMap[status] ?? 'assigner.status_no_status'
    );
  }

  async applyAssignament(executionId: string): Promise<void> {
    const request: ApplyAssignmentRequest = {
      executionId,
      forcefulOverride: true,
    };
    this.isApplyingAssignment = true;
    try {
      await firstValueFrom(this.assignerService.applyAssignment(request));
      this.closeModal();
      this.toApplyAssignamentId = '';
      this.toApplyAssignamentDate = '';
      this.isApplyingAssignment = false;
      this.setLoading(true);
      await this.refresh();
      this.showSuccessAlert(
        this.translate.instant('assigner.assignment_applied_success')
      );
    } catch (err) {
      this.isApplyingAssignment = false;
      this.assignerService.publish('errorApplyAssign', request);
      this.showErrorAlert(
        this.translate.instant('assigner.error_applying_assignment')
      );
      console.error('Error applying assignment:', err);
    }
  }

  tryApplyAssignament(assignament: AssignmentExecutionView): void {
    const found = this.allAssignments.find((obj) => {
      if (!assignament.assignmentDateFormatted || !obj.assignmentDateFormatted) {
        return false;
      }

      const assignmentDate = DateFormatter.parseDate(
        assignament.assignmentDateFormatted
      );
      const objDate = DateFormatter.parseDate(obj.assignmentDateFormatted);

      return (
        (obj.status === 'InProgress' || obj.status === 'Applied') &&
        assignmentDate.getTime() === objDate.getTime() &&
        obj.zoneName === assignament.zoneName
      );
    });

    if (found) {
      this.toApplyAssignamentId = assignament.executionId;
      this.toApplyAssignamentDate = `${assignament.zoneName},${assignament.assignmentDateFormatted}`;
      this.openModal(this.warningModal);
    } else {
      void this.applyAssignament(assignament.executionId);
    }
  }

  setSortColumn(column: SortableColumn): void {
    if (this.sortedColumn === column) {
      if (this.sortDirection === 1) {
        this.sortDirection = -1;
      } else if (this.sortDirection === -1) {
        this.sortDirection = null;
        this.sortedColumn = null;
      }
    } else {
      this.sortedColumn = column;
      this.sortDirection = 1;
    }
    this.applyFilters();
  }

  viewAssignamentDetails(id: string): void {
    this.assignamentId.emit(id);
  }

  openModal(warningModal: TemplateRef<void>): void {
    this.bsModalRef = this.modalService.show(warningModal);
  }

  closeModal(): void {
    if (this.bsModalRef) this.bsModalRef.hide();
  }
}
